import { useEffect, useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import './StatsPage.css';

function calcLongestStreak(dates) {
  if (dates.length === 0) return 0;
  const sorted = [...new Set(dates)].sort();
  let max = 1, cur = 1;
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1] + 'T00:00:00');
    const curr = new Date(sorted[i] + 'T00:00:00');
    if ((curr - prev) / 86400000 === 1) {
      cur++;
      if (cur > max) max = cur;
    } else {
      cur = 1;
    }
  }
  return max;
}

export default function StatsPage({ user, currentProfile, onBack }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    (async () => {
      const query = supabase
        .from('records')
        .select('start_date, category_name, category_emoji, location_name, is_ongoing')
        .eq('user_id', user.id);

      if (currentProfile?.id) {
        const { data: shares } = await supabase
          .from('record_shares')
          .select('record_id')
          .eq('profile_id', currentProfile.id);
        if (shares) {
          const ids = shares.map(s => s.record_id);
          if (ids.length === 0) { setLoading(false); return; }
          query.in('id', ids);
        }
      }

      const { data } = await query;
      setRecords(data || []);
      setLoading(false);
    })();
  }, [user.id, currentProfile?.id]);

  const years = useMemo(() => {
    const set = new Set();
    records.forEach(r => { if (r.start_date) set.add(r.start_date.slice(0, 4)); });
    return [...set].sort().reverse();
  }, [records]);

  const yearRecords = useMemo(() =>
    selectedYear ? records.filter(r => r.start_date?.startsWith(selectedYear)) : records,
  [records, selectedYear]);

  // 요약 통계
  const stats = useMemo(() => {
    const dates = yearRecords.map(r => r.start_date).filter(Boolean);
    const uniqueDays = new Set(dates).size;
    const longestStreak = calcLongestStreak(dates);

    // 월별
    const monthMap = {};
    dates.forEach(d => {
      const m = parseInt(d.slice(5, 7), 10);
      monthMap[m] = (monthMap[m] || 0) + 1;
    });
    let bestMonth = null, bestMonthCount = 0;
    Object.entries(monthMap).forEach(([m, c]) => {
      if (c > bestMonthCount) { bestMonth = parseInt(m, 10); bestMonthCount = c; }
    });

    return { total: yearRecords.length, uniqueDays, longestStreak, bestMonth, bestMonthCount, monthMap };
  }, [yearRecords]);

  // 카테고리 비율
  const categoryStats = useMemo(() => {
    const map = {};
    yearRecords.forEach(r => {
      const key = r.category_name || '미분류';
      if (!map[key]) map[key] = { name: key, emoji: r.category_emoji || '📦', count: 0 };
      map[key].count++;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [yearRecords]);

  const maxCatCount = categoryStats.length > 0 ? categoryStats[0].count : 1;

  // 월별 기록 수
  const monthlyData = useMemo(() => {
    const data = [];
    for (let m = 1; m <= 12; m++) {
      data.push({ month: m, count: stats.monthMap[m] || 0 });
    }
    return data;
  }, [stats.monthMap]);

  const maxMonthCount = Math.max(...monthlyData.map(d => d.count), 1);

  // 연도별 추이
  const yearlyTrend = useMemo(() => {
    const map = {};
    records.forEach(r => {
      if (!r.start_date) return;
      const y = r.start_date.slice(0, 4);
      map[y] = (map[y] || 0) + 1;
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b))
      .map(([year, count]) => ({ year, count }));
  }, [records]);

  const maxYearCount = Math.max(...yearlyTrend.map(d => d.count), 1);

  // 장소 TOP 5
  const topLocations = useMemo(() => {
    const map = {};
    yearRecords.forEach(r => {
      if (!r.location_name) return;
      map[r.location_name] = (map[r.location_name] || 0) + 1;
    });
    return Object.entries(map).sort(([, a], [, b]) => b - a).slice(0, 5)
      .map(([name, count]) => ({ name, count }));
  }, [yearRecords]);

  // 진행 중 기록
  const ongoingCount = useMemo(() =>
    yearRecords.filter(r => r.is_ongoing).length,
  [yearRecords]);

  if (loading) {
    return (
      <div className="stats-page">
        <header className="stats-header">
          <button className="stats-back" onClick={onBack}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <span className="stats-title">통계</span>
        </header>
        <div className="stats-loading">불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="stats-page">
      <header className="stats-header">
        <button className="stats-back" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <span className="stats-title">통계</span>
      </header>

      <div className="stats-body">
        {/* 연도 선택 */}
        {years.length > 0 && (
          <div className="stats-year-tabs">
            <button
              className={`stats-year-tab${selectedYear === null ? ' stats-year-tab--active' : ''}`}
              onClick={() => setSelectedYear(null)}
            >
              전체
            </button>
            {years.map(y => (
              <button
                key={y}
                className={`stats-year-tab${selectedYear === y ? ' stats-year-tab--active' : ''}`}
                onClick={() => setSelectedYear(y)}
              >
                {y}
              </button>
            ))}
          </div>
        )}

        {/* 요약 카드 */}
        <div className="stats-summary">
          <div className="stats-card">
            <span className="stats-card-icon">📝</span>
            <span className="stats-card-value">{stats.total}</span>
            <span className="stats-card-label">총 기록</span>
          </div>
          <div className="stats-card">
            <span className="stats-card-icon">📅</span>
            <span className="stats-card-value">{stats.uniqueDays}</span>
            <span className="stats-card-label">기록한 날</span>
          </div>
          <div className="stats-card">
            <span className="stats-card-icon">🔥</span>
            <span className="stats-card-value">{stats.longestStreak}</span>
            <span className="stats-card-label">최장 연속</span>
          </div>
          <div className="stats-card">
            <span className="stats-card-icon">🔄</span>
            <span className="stats-card-value">{ongoingCount}</span>
            <span className="stats-card-label">진행 중</span>
          </div>
        </div>

        {stats.bestMonth && (
          <div className="stats-highlight">
            🏆 가장 많이 기록한 달: <strong>{stats.bestMonth}월</strong> ({stats.bestMonthCount}개)
          </div>
        )}

        {/* 월별 기록 */}
        {selectedYear && (
          <div className="stats-section">
            <h3 className="stats-section-title">월별 기록 수</h3>
            <div className="stats-bar-chart">
              {monthlyData.map(d => (
                <div key={d.month} className="stats-bar-row">
                  <span className="stats-bar-label">{d.month}월</span>
                  <div className="stats-bar-track">
                    <div
                      className="stats-bar-fill"
                      style={{ width: `${(d.count / maxMonthCount) * 100}%` }}
                    />
                  </div>
                  <span className="stats-bar-value">{d.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 카테고리 비율 */}
        {categoryStats.length > 0 && (
          <div className="stats-section">
            <h3 className="stats-section-title">카테고리별</h3>
            <div className="stats-bar-chart">
              {categoryStats.map(cat => (
                <div key={cat.name} className="stats-bar-row">
                  <span className="stats-bar-label stats-bar-label--cat">
                    {cat.emoji} {cat.name}
                  </span>
                  <div className="stats-bar-track">
                    <div
                      className="stats-bar-fill stats-bar-fill--cat"
                      style={{ width: `${(cat.count / maxCatCount) * 100}%` }}
                    />
                  </div>
                  <span className="stats-bar-value">
                    {cat.count} <span className="stats-bar-pct">({Math.round(cat.count / stats.total * 100)}%)</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 연도별 추이 */}
        {yearlyTrend.length > 1 && (
          <div className="stats-section">
            <h3 className="stats-section-title">연도별 기록 추이</h3>
            <div className="stats-trend-chart">
              {yearlyTrend.map(d => (
                <div key={d.year} className="stats-trend-col">
                  <span className="stats-trend-value">{d.count}</span>
                  <div className="stats-trend-bar-wrap">
                    <div
                      className="stats-trend-bar"
                      style={{ height: `${(d.count / maxYearCount) * 100}%` }}
                    />
                  </div>
                  <span className="stats-trend-label">{d.year.slice(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 장소 TOP 5 */}
        {topLocations.length > 0 && (
          <div className="stats-section">
            <h3 className="stats-section-title">📍 자주 기록한 장소</h3>
            <div className="stats-location-list">
              {topLocations.map((loc, i) => (
                <div key={loc.name} className="stats-location-row">
                  <span className="stats-location-rank">{i + 1}</span>
                  <span className="stats-location-name">{loc.name}</span>
                  <span className="stats-location-count">{loc.count}회</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {records.length === 0 && (
          <div className="stats-empty">
            <p>아직 기록이 없어요.</p>
            <p>첫 번째 기록을 추가해보세요!</p>
          </div>
        )}
      </div>
    </div>
  );
}
